/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/require-tagless-components */
// Ember imports
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import config from '@linc-technologies/linced-common/utils/config';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { action } from '@ember/object';

export default class TotpCode extends Component {
  // Services
  @service
  fetch;

  @service
  session;

  // Attributes
  // secret is a base32Encoded string passed during enrolment
  secret = null;

  // isEnrolment indicates that the component is used during the MFA enrolment process
  isEnrolment = false;

  // methodId is the method to remove
  methodId = '';

  // message is a validation warning message displayed to users
  @tracked
  message = '';

  // user is the user who the un-enrolment is for
  user = null;

  // codeArray is an array of digits entered by the user
  @tracked
  codeArray = [];

  // isProcessing is true if there is a request being processed
  @tracked
  isProcessing = false;

  // Lifecycle hooks
  didInsertElement() {
    super.didInsertElement(...arguments);
    // When the component is re-inserted into the DOM, clear the inputs, so the
    // template re-renders with no digits
    this.clearCode();
  }

  // Functions
  // clearCodes resets the array and focuses on the first child input element
  clearCode() {
    // Clear the code by setting it to a new array,
    // so Ember detects the change and re-renders the template
    this.codeArray = [];
    // Re-focus to the first input field to re-start the code
    this.element.querySelector(`#${this.childViews[0].inputElementId}`).focus();
  }

  // enrolToMFA sends a request to create a new MFA method and performs additional
  // actions for the UI
  enrolToMFA() {
    this.isProcessing = true;
    // FusionAuth automatically does the conversion for us and
    // requests personId
    let userId = this.session.get('user.id');
    let code = this.codeArray.join('');

    // Send a request to enrol to MFA
    let uri = `${config.APP.apiHost}/auth/v4/users/${userId}/mfa`;
    let data = {
      userId: userId,
      secretBase32Encoded: this.secret,
      code: code,
    };
    let params = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(uri, params)
      .then((res) => {
        if (!isEmpty(res.recoveryCodes)) {
          // Recovery codes are only returned on the first MFA method's addition
          // send recovery codes to the parent component to display
          this.setRecoveryCodes(res.recoveryCodes);
        }
        // Move on to the next step in the paper-stepper component
        this.nextStep();
      })
      .catch(() => {
        this.clearCode();
        this.message = 'Invalid code. Please try again';
      });
    this.isProcessing = false;
  }

  // removeMFA sends a request to remove an MFA method and performs additional
  // actions for the UI
  removeMFA() {
    this.isProcessing = true;
    // FusionAuth automatically does the conversion for us and
    // requests personId
    let userId = this.user.get('id');
    let code = this.codeArray.join('');

    // Send a request to remove an MFA method
    let uri = `${config.APP.apiHost}/auth/v4/users/${userId}/mfa/${this.methodId}`;
    let data = {
      userId: userId,
      methodId: this.methodId,
      code: code,
    };
    let params = {
      data: JSON.stringify(data),
    };
    this.fetch
      .delete(uri, params)
      .then(() => {
        // Move on to the next step in the paper-stepper component
        this.nextStep();
      })
      .catch(() => {
        this.clearCode();
        this.message = 'Invalid code. Please try again';
      });
    this.isProcessing = false;
  }

  // focusNext focuses on the next child input element given the current index
  focusNext(index) {
    if (index + 1 < this.childViews.length) {
      // We're still inside the existent children
      let nextElement = this.childViews[index + 1].inputElementId;
      if (!isEmpty(nextElement)) {
        // Move on to the next input
        this.element
          .querySelector(`#${this.childViews[index + 1].inputElementId}`)
          .focus();
      }
    }
  }

  // focusPrevious focuses on the previous child input element given the current index
  focusPrevious(index) {
    if (index !== 0) {
      // We're still inside the existent children
      let previousElement = this.childViews[index - 1].inputElementId;
      if (!isEmpty(previousElement)) {
        // Move on to the previous input
        this.element
          .querySelector(`#${this.childViews[index - 1].inputElementId}`)
          .focus();
      }
    }
  }

  // isNumeric checks whether the value is numeric
  isNumeric(value) {
    return /^\d+$/.test(value);
  }

  // setCodeDigit replaces the digit at index, rebuilding and reassigning
  // codeArray so the @tracked field change is detected and the template
  // re-renders (in-place mutation of a tracked array isn't detected).
  setCodeDigit(index, value) {
    let codeArray = [...this.codeArray];
    codeArray[index] = value;
    this.codeArray = codeArray;
  }

  // changeCodeDigit validates the digit, mutates the value,
  // and performs additional actions
  changeCodeDigit(index, value) {
    // Validate the input
    if (!this.isNumeric(value)) {
      // It's not a number, do not set it
      this.message = 'Only digits are allowed';
      this.setCodeDigit(index, '');
      return;
    }
    if (!isEmpty(this.codeArray[index])) {
      // The user is re-doing a value
      if (value.length >= 2) {
        // The user re-typed without highlighting/removing the value
        // Grab only the latest input, discard the previous one
        value = value.replace(this.childViews[index]._prevValue, '');
      }
    }

    // Mutate the value in the array
    this.setCodeDigit(index, value);

    if (this.codeArray.length === 6) {
      // All digits of the TOTP code are filled in, take appropriate action
      if (this.isEnrolment) {
        // We're enrolling to MFA
        this.enrolToMFA();
        return;
      }
      // We're removing an MFA method
      this.removeMFA();
    }
    // Attempt to automatically shift focuses
    this.focusNext(index);
  }

  // removeChar is called when a user uses a backspace or del buttons
  // to remove a character from the code
  removeChar(index) {
    if (!isEmpty(this.codeArray[index])) {
      // The user is deleting an existing value
      this.setCodeDigit(index, '');
      return;
    }
    // Otherwise, the user is trying to move to the previous value
    this.focusPrevious(index);
    this.setCodeDigit(index - 1, '');
  }

  // pasteCode is called when a user pastes more than 3 characters in any of the
  // input fields
  pasteCode(index, value) {
    if (value.length > 6) {
      this.message = 'Codes may not contain more than 6 digits.';
      return;
    }
    // Split the string and try each character
    let chars = value.split('');
    chars.forEach((char) => {
      // Start filling from the start regardless of where the user pasted
      this.changeCodeDigit(chars.indexOf(char), char);
    });
  }

  @action
  handleInput(index, event) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      // The user is using a backspace or delete
      this.removeChar(index);
    }
  }

  @action
  changeCode(index, value) {
    // Clear out the message
    this.message = '';
    if (!isEmpty(value)) {
      // The user has actually input something instead of using a system button
      if (value.length > 3) {
        // The user has pasted a code
        this.pasteCode(index, value);
      } else {
        // The user has input a single digit
        // Or is re-doing an existent value
        this.changeCodeDigit(index, value);
      }
    }
  }
}
